import { DeleteRounded } from '@mui/icons-material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Box,
  Card,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import usePost from '../../hooks/usePost';
import { FileUploadButton } from '../../pages/library/fileUploadButton';
import { putPost } from '../../redux/services/postService';
import {
  SAS_MAX_SIZE_BYTES,
  uploadFileViaSAS,
} from '../../redux/services/uploadsService';
import { toReferenceId } from '../../shared/api/referenceId';
import { getDisplayFileName } from '../../shared/files/fileType';
import TextEditor from '../editor';
import { Preview } from './utils';

type CommonProps = {
  files: Array<Preview>;
  setFiles: (_: Array<Preview>) => void;
  editorState: EditorState;
  setEditorState: (_: EditorState) => void;
  addFiles: (_: Array<File>) => void;
  removeFile: (_: number) => void;
  submitDisabled: boolean;
  loading: boolean;
  handleSubmit: () => Promise<boolean>;
};

const Common = (props: CommonProps) => {
  const {
    files,
    setFiles,
    editorState,
    setEditorState,
    addFiles,
    removeFile,
    submitDisabled,
    loading,
    handleSubmit,
  } = props;
  return (
    <Card sx={{}}>
      <Box sx={{ flexGrow: 1 }}>
        <TextEditor onChange={setEditorState} editorState={editorState} />
      </Box>

      <Stack>
        {files.map((file) => {
          return (
            <Stack
              key={file.fileUrl}
              direction={'row'}
              justifyContent={'space-between'}
            >
              <Typography key={file.name}>{file.name}</Typography>
              <IconButton onClick={() => removeFile(files.indexOf(file))}>
                <DeleteRounded />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>

      <Stack justifyContent={'end'} spacing={1} direction={'row'}>
        <FileUploadButton
          multiple
          onSelect={(file) => {
            addFiles([file]);
          }}
        />
        <IconButton
          onClick={async () => {
            await handleSubmit();
          }}
          disabled={submitDisabled}
          loading={loading}
          variant="solid"
          color="primary"
        >
          <SendRoundedIcon />
        </IconButton>
      </Stack>
    </Card>
  );
};

type Props = {
  postId: string | null;
  setOpen: (_: boolean) => void;
  isOpen: boolean;
  asModal?: boolean;
  onPostSubmit?: () => Promise<void>;
};

export default function PostModal(props: Readonly<Props>) {
  const { postId, setOpen, isOpen, asModal, onPostSubmit } = props;
  const [loading, setLoading] = useState(false);
  const postQuery = usePost(postId ?? null);
  const [files, setFiles] = useState<Array<Preview>>([]);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const params = useParams();

  const close = () => {
    setOpen(false);
  };

  const submitDisabled =
    editorState.getCurrentContent().getPlainText().length === 0;

  useMemo(() => {
    try {
      // This means we are in edit mode, so we need to update the initial state to reflect the state of the post
      if (postId) {
        if (!postQuery.isSuccess) return;

        const post = postQuery.data;
        const rawState = convertFromRaw(JSON.parse(postQuery.data.body));
        const parsedState = EditorState.createWithContent(rawState);
        setEditorState(parsedState);
        // Show the original filename, not the full blob URL. Newly-added
        // files below use `file.name`, so listing stored ones by URL made the
        // dialog inconsistent and unreadable.
        const loadedFiles: Array<Preview> = post.fileUrls.map(
          (fileUrl: string) => ({
            fileUrl,
            name: getDisplayFileName(fileUrl),
            file: null,
            uploaded: true,
          })
        );
        setFiles(loadedFiles);
      } else {
        // Not editing, initialize the state
        setEditorState(EditorState.createEmpty());
        setFiles([]);
      }
    } catch (error) {
      console.error(error);
    }
  }, [postQuery.data, postId]);

  const addFiles = (newFiles: Array<File>) => {
    // Pre-flight size cap: matches server-side SAS_MAX_SIZE_BYTES so we
    // surface the rejection in the file picker rather than after a
    // 200 MB upload completes and the /uploads/sas call returns 400.
    const oversize = newFiles.find((file) => file.size > SAS_MAX_SIZE_BYTES);
    if (oversize) {
      const cap = SAS_MAX_SIZE_BYTES / (1024 * 1024);
      toast.error(
        `${oversize.name} is te groot (max ${cap} MB). Verklein het bestand of host het elders.`
      );
      return;
    }

    const newPreviews: Array<Preview> = newFiles.map((file) => {
      return {
        fileUrl: URL.createObjectURL(file),
        name: file.name,
        file,
        uploaded: false,
      };
    });
    setFiles((oldFiles: Array<Preview>) => [...oldFiles, ...newPreviews]);
  };

  const removeFile = (indexToRemove: number) => {
    setFiles((oldFiles: Array<Preview>) =>
      oldFiles.filter((_, index: number) => index !== indexToRemove)
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Convert the editor state to raw text
      const post = postQuery.data;
      const body = JSON.stringify(
        convertToRaw(editorState.getCurrentContent())
      );

      // Filter out the files that have already been uploaded
      const filesToUpload = files.filter(
        (preview: Preview) => !preview.uploaded
      );

      // Upload the remaining files directly to Azure Blob via SAS — bypasses
      // App Service body-cap and timeout that hung MP4 uploads on the legacy
      // /file-upload-no-library path.
      const promises = filesToUpload.map((preview: Preview) =>
        uploadFileViaSAS(preview.file)
      );
      const newFiles = await Promise.all(promises);
      const oldFiles = files
        .filter((preview: Preview) => preview.uploaded)
        .map((preview: Preview) => preview.fileUrl);

      // Combine the old and new file urls
      const combinedFiles = [...oldFiles, ...newFiles];

      // GET /posts/:postId populates `topic` and `event`, so in edit mode
      // these are full documents, not id strings. Sending them straight back
      // made PUT /posts fail Zod validation with
      // `Expected string, received object at "body.topic"`, which blocked
      // every post edit. Normalise to the id the server contract expects.
      const topicId = postId ? toReferenceId(post.topic) : params.topicId;
      const eventId = postId ? toReferenceId(post.event) : params.eventId;

      // Act
      await putPost(postId ?? null, body, combinedFiles, topicId, eventId);

      // Reset the state
      setFiles([]);
      setEditorState(EditorState.createEmpty());
      if (onPostSubmit) {
        await onPostSubmit();
      }
      await postQuery.refetch();
      setLoading(false);
      close();
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      // Surface the failure to the user — previously this catch only
      // console.errored, which combined with the axios interceptor's
      // network-error crash made the whole flow look like "nothing
      // happened" when a post-create call failed (network, 4xx, 5xx,
      // or a thrown precondition like missing topicId/eventId). The
      // toast turns silent-fail into actionable feedback.
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Bericht kon niet worden geplaatst.';
      toast.error(message);
      return false;
    }
    return true;
  };

  if (asModal) {
    return (
      <Modal open={isOpen} onClose={close}>
        <ModalDialog sx={{ minWidth: '50vw' }}>
          <ModalClose />
          <Typography>Post wijzigen</Typography>
          <Common
            files={files}
            setFiles={setFiles}
            editorState={editorState}
            setEditorState={setEditorState}
            addFiles={addFiles}
            removeFile={removeFile}
            submitDisabled={submitDisabled}
            loading={loading}
            handleSubmit={handleSubmit}
          />
        </ModalDialog>
      </Modal>
    );
  }

  return (
    <Common
      files={files}
      setFiles={setFiles}
      editorState={editorState}
      setEditorState={setEditorState}
      addFiles={addFiles}
      removeFile={removeFile}
      submitDisabled={submitDisabled}
      loading={loading}
      handleSubmit={handleSubmit}
    />
  );
}
